import { ref, computed, watchEffect } from 'vue'
import { themes, colors, defaultTheme, darkThemeKeys } from '@/config/theme.js'

const STORAGE_KEY = 'usenlease-theme'

// Shared reactive state (singleton across all component instances)
const currentTheme = ref(localStorage.getItem(STORAGE_KEY) || defaultTheme)

// Pending click event for circular reveal animation
let _pendingEvent = null

function applyTheme(themeName) {
  const t = themes[themeName]
  if (!t) return
  const root = document.documentElement

  // Set theme data attribute + class for Tailwind dark:
  root.setAttribute('data-theme', themeName)
  const isDarkTheme = darkThemeKeys.includes(themeName)
  if (isDarkTheme) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }

  // Apply CSS variables for all theme tokens
  Object.entries(t).forEach(([key, value]) => {
    if (key !== 'name') {
      root.style.setProperty(`--color-${key}`, value)
    }
  })

  // Apply brand color variables
  Object.entries(colors.primary).forEach(([shade, hex]) => {
    root.style.setProperty(`--color-primary-${shade}`, hex)
  })
  Object.entries(colors.secondary).forEach(([shade, hex]) => {
    root.style.setProperty(`--color-secondary-${shade}`, hex)
  })
  Object.entries(colors.neutral).forEach(([shade, hex]) => {
    root.style.setProperty(`--color-neutral-${shade}`, hex)
  })

  localStorage.setItem(STORAGE_KEY, themeName)
}

function applyThemeWithTransition(themeName) {
  const event = _pendingEvent
  _pendingEvent = null

  // Fallback: no View Transitions API or no click coords
  if (!document.startViewTransition || !event) {
    applyTheme(themeName)
    return
  }

  const x = event.clientX
  const y = event.clientY
  const endRadius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y)
  )

  const transition = document.startViewTransition(() => {
    applyTheme(themeName)
  })

  transition.ready.then(() => {
    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${endRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 500,
        easing: 'ease-in-out',
        pseudoElement: '::view-transition-new(root)',
      }
    )
  })
}

export function useTheme() {
  const isDark = computed(() => darkThemeKeys.includes(currentTheme.value))
  const isLight = computed(() => currentTheme.value === 'light')

  function toggleTheme(event) {
    if (event) _pendingEvent = event
    if (isDark.value) {
      currentTheme.value = 'light'
    } else {
      // Restore user's preferred dark variant
      const preferred = localStorage.getItem('usenlease-dark-variant') || 'dark'
      currentTheme.value = darkThemeKeys.includes(preferred) ? preferred : 'dark'
    }
  }

  function setTheme(name, event) {
    if (themes[name]) {
      if (event) _pendingEvent = event
      currentTheme.value = name
    }
  }

  // Watch and apply whenever theme changes
  watchEffect(() => {
    applyThemeWithTransition(currentTheme.value)
  })

  return {
    currentTheme,
    isDark,
    isLight,
    toggleTheme,
    setTheme,
    themes: Object.keys(themes),
    darkThemeKeys,
  }
}
